
export enum Screen {
  DASHBOARD = 'DASHBOARD',
  BRAND_SELECT = 'BRAND_SELECT',
  WIZARD_INPUT = 'WIZARD_INPUT',
  EDITOR = 'EDITOR',
  SETTINGS = 'SETTINGS',
}

export enum Platform {
  YOUTUBE = 'YouTube',
  INSTAGRAM = 'Instagram',
}

export enum Emotion {
  SHOCK = '😱 שוק',
  ANGER = '😡 כעס',
  ENTHUSIASM = '🤩 התלהבות',
  AUTHORITY = '😎 סמכותיות',
  SAD = '😢 עצב',
  MYSTERIOUS = '🤫 מסתורין',
  CONFUSION = '🤔 בלבול',
  DETERMINATION = '💪 נחישות',
  PLAYFUL = '😏 שובבות',
  AWE = '🤯 תדהמה',
  VICTORY = '🏆 ניצחון',
}

export enum Style {
  REALISTIC = 'ריאליסטי',
  VIVID_POP = 'ויראלי / הייפ',
  CORPORATE = 'עסקי נקי',
  DARK_NEON = 'ניאון וגיימינג',
  CINEMATIC = 'קולנועי דרמטי',
  ORGANIC = 'אסתטי טבעי',
  RETRO_VHS = 'רטרו ונוסטלגיה',
  COLLAGE = 'קולאז\' אומנותי',
  GRUNGE = 'מחוספס וחזק',
  COMIC = 'קומיקס',
  MINECRAFT = 'מיינקראפט',
  MINIMAL = 'מינימליסטי',
  ANIME = 'אנימה',
  SURREAL = 'סוריאליסטי',
  OTHER = 'אחר',
}

export interface Character {
  id: string;
  name: string;
  images: string[]; // base64 array, max 5
}

export interface Brand {
  id: string;
  name: string; // Brand Name
  businessDescription: string;
  niche: string;
  targetAudience: string;
  preferredColors?: string;
  preferredFont?: string;
  preferredPlatform?: Platform;
  characters: Character[];
}

export type TextZone = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-bar';

export interface ThumbnailConcept {
  id: string;
  title: string; // Image-accurate: describes exactly what the thumbnail image will show
  /** 1-3 words (Hebrew), rendered as text inside the thumbnail image. User can edit in concept selection. */
  mainTitle?: string;
  descriptionHe?: string; // Legacy: no longer generated, optional for backward compatibility
  visualDescription: string; // English Prompt for AI
  suggestedText?: string; // Legacy: was overlay suggestion; mainTitle is now baked into image
  elements: string[];
  rationale: string[]; // Checklist
  textZone?: TextZone; // Recommended area for text overlay
}

// Variation with its own texts - each variation is independent
export interface Variation {
  id: string;
  imageUrl: string;       // URL from Firebase Storage (not base64)
  texts: TextObject[];    // Texts specific to THIS variation
  createdAt: number;
  /** Concept used to generate this variation; used by "התאם אוטומטית" to adapt the right idea */
  concept?: ThumbnailConcept;
}

/** Stored snapshot of user choices when creating the thumbnail (for review/analytics). No base64 images. */
export interface StoredGenerationParams {
  emotion: Emotion;
  style: Style; // Can be custom string if 'Other' was used
  extraDetails?: string;
  /** Explicit choice: with face (character) vs without. Omitted on legacy projects; infer from characterId. */
  withFace?: boolean;
  characterId?: string;
  /** Display name of chosen character when withFace is true; avoids joining with Brand for analytics. */
  characterName?: string;
  hasSecondPerson?: boolean;
  hasLogo?: boolean;
}

/** Status of server-side idea generation. Used when generation runs in Cloud Functions. */
export type ProjectGenerationStatus = 'pending_ideas' | 'ideas_done' | 'ideas_error';

export interface Project {
  id: string;
  brandId: string;
  name: string; // Video Topic
  platform: Platform;
  variations: Variation[]; // Each variation has its own image and texts
  createdAt: number;
  concept?: ThumbnailConcept; // Saved concept for re-generation
  generationParams?: StoredGenerationParams; // User choices (emotion, style, etc.) for review/analytics
  /** Set when project is created before ideas exist; Cloud Function updates to ideas_done or ideas_error. */
  generationStatus?: ProjectGenerationStatus;
  // Legacy field for migration - will be removed after migration
  texts?: TextObject[]; // Deprecated: use variations[].texts instead
}

export interface GenerationParams {
  topic: string;
  characterId?: string; // from Brand
  emotion: Emotion;
  style: Style; // Can be custom string if 'Other' is used logic-wise
  extraDetails: string;
  platform: Platform;
  hasSecondPerson: boolean;
  secondPersonImage?: string; // base64
  hasLogo: boolean;
  logoImage?: string; // base64
}

export interface ChatMessage {
  role: 'user' | 'model';
  text: string;
  image?: string; // For model responses that include a generated image
}

export interface TextObject {
    id: string;
    text: string;
    x: number;
    y: number;
    fontSize: number;
    color: string;
    fontFamily: string;
    isDragging: boolean;
    strokeColor: string;
    strokeWidth: number;
    rotation: number; // in degrees
    
    // Background
    // Background
    isBackgroundEnabled: boolean;
    backgroundColor: string;
    backgroundOpacity: number; // 0-100
    backgroundPadding: number;
    backgroundRadius: number;

    // Shadow
    isShadowEnabled: boolean;
    shadowColor: string;
    shadowBlur: number;
    shadowOpacity: number; // 0-1
    shadowDistance: number;
    shadowAngle: number; // 0-360 degrees

    // Neon
    neonColor: string;
    neonIntensity: number; // 0 = off, > 0 = blur radius
}

export interface TextStyleTemplate {
    id: string;
    name: string;
    style: Partial<TextObject>;
    isDefault?: boolean;
}

// User & Credits Types
export type SubscriptionTier = 'free' | 'creator' | 'viral' | 'studio';

export interface UserData {
    uid: string;
    email: string;
    displayName?: string;
    photoURL?: string;
    subscription: SubscriptionTier;
    credits: number;
    creditsResetDate: Date;
    createdAt: Date;
}
